import React, { useState, useEffect } from 'react';
import { Input, Button, Popconfirm } from 'antd';
import { DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import FormItemWrapper from '~/components/common/FormItemWrapper/FormItemWrapper';
import { ElementAPIs } from '~/common/typescript/apis/ElementAPIs';
import { useAtom } from 'jotai';
import { elementsAtom, Element } from '../../Element/ElementStore';

interface AttributesManagementProps {
  selectedElement: Element;
}

export const AttributesManagement: React.FC<AttributesManagementProps> = ({ selectedElement }) => {
  const [elements, setElements] = useAtom(elementsAtom);
  const [attributes, setAttributes] = useState<Record<string, string>>({});
  const [newAttributeName, setNewAttributeName] = useState('');
  const [newAttributeValue, setNewAttributeValue] = useState('');
  const [editingAttribute, setEditingAttribute] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  // Load attributes from selectedElement
  useEffect(() => {
    if (!selectedElement) return;

    let existingMetadata = {};
    try {
      existingMetadata = JSON.parse(selectedElement.metadata || '{}');
    } catch (error) {
      existingMetadata = {};
    }

    const existingAttributes = (existingMetadata as any).attributes || {};
    setAttributes(existingAttributes);
  }, [selectedElement]);

  // Update attributes when they change
  useEffect(() => {
    if (!selectedElement) return;

    const timer = setTimeout(() => {
      doUpdateAttributes();
    }, 1000);

    return () => clearTimeout(timer);
  }, [attributes]);

  const doUpdateAttributes = async () => {
    if (!selectedElement) return;

    let existingMetadata = {};
    try {
      existingMetadata = JSON.parse(selectedElement.metadata || '{}');
    } catch (error) {
      existingMetadata = {};
    }

    const metadataObject = {
      ...existingMetadata,
      attributes: attributes
    };

    await ElementAPIs.remote_update(selectedElement.uuid, {
      metadata: metadataObject,
    });

    setElements(elements.map((element) => {
      if (element.uuid === selectedElement.uuid) {
        return {
          ...element,
          metadata: JSON.stringify(metadataObject)
        };
      }
      return element;
    }));
  };

  const addAttribute = () => {
    if (!newAttributeName.trim() || newAttributeName in attributes) {
      return;
    }
    setAttributes({
      ...attributes,
      [newAttributeName]: newAttributeValue
    });
    setNewAttributeName('');
    setNewAttributeValue('');
  };

  const deleteAttribute = (key: string) => {
    const newAttributes = { ...attributes };
    delete newAttributes[key];
    setAttributes(newAttributes);
  };

  const startEditAttribute = (key: string) => {
    setEditingAttribute(key);
    setEditingValue(attributes[key]);
  };

  const saveEditAttribute = () => {
    if (editingAttribute) {
      setAttributes({
        ...attributes,
        [editingAttribute]: editingValue
      });
    }
    setEditingAttribute(null);
    setEditingValue('');
  };

  const cancelEditAttribute = () => {
    setEditingAttribute(null);
    setEditingValue('');
  };

  return (
    <>
      {/* Add new attribute form */}
      <FormItemWrapper label="Add new attribute" hideLabel={true}>
        <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
          <Input
            size="small"
            placeholder="Name"
            value={newAttributeName}
            onChange={(e) => setNewAttributeName(e.target.value)}
            onPressEnter={addAttribute}
            style={{ flex: 1 }}
          />
          <Input
            size="small"
            placeholder="Value"
            value={newAttributeValue}
            onChange={(e) => setNewAttributeValue(e.target.value)}
            onPressEnter={addAttribute}
            style={{ flex: 1 }}
          />
        </div>
        <Button
          size="small"
          type="primary"
          onClick={addAttribute}
          disabled={!newAttributeName.trim()}
          style={{ width: '100%' }}
        >
          Add attribute
        </Button>
      </FormItemWrapper>

      {/* Attributes list */}
      {Object.keys(attributes).length === 0 ? (
        <div style={{ color: '#999', fontSize: '14px', textAlign: 'center', padding: '20px 0' }}>
          No attributes yet
        </div>
      ) : (
        <div>
          {Object.entries(attributes).map(([key, value]) => (
            <div key={key} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '2px' }}>
                  {key}
                </div>
                {editingAttribute === key ? (
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <Input
                      size="small"
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onPressEnter={saveEditAttribute}
                      style={{ flex: 1 }}
                      autoFocus
                    />
                    <Button
                      size="small"
                      type="text"
                      icon={<CheckOutlined />}
                      onClick={saveEditAttribute}
                      style={{ color: '#52c41a' }}
                    />
                    <Button
                      size="small"
                      type="text"
                      icon={<CloseOutlined />}
                      onClick={cancelEditAttribute}
                      style={{ color: '#ff4d4f' }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#666',
                      cursor: 'pointer',
                      padding: '2px 4px',
                      borderRadius: '2px',
                      backgroundColor: '#f8f8f8'
                    }}
                    onClick={() => startEditAttribute(key)}
                  >
                    {value || 'Click to edit'}
                  </div>
                )}
              </div>
              <div style={{ marginLeft: '8px', display: 'flex', gap: '4px' }}>
                {editingAttribute !== key && (
                  <>
                    <Button
                      size="small"
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => startEditAttribute(key)}
                      style={{ color: '#1890ff' }}
                    />
                    <Popconfirm
                      title="Delete attribute"
                      description="Are you sure you want to delete this attribute?"
                      onConfirm={() => deleteAttribute(key)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        size="small"
                        type="text"
                        icon={<DeleteOutlined />}
                        style={{ color: '#ff4d4f' }}
                      />
                    </Popconfirm>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};